import {
  Button,
  Card,
  CardContent,
  Divider,
  makeStyles,
  Menu,
  MenuItem,
  SvgIcon,
  Typography,
  useTheme,
} from '@material-ui/core';

import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import type React from 'react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

const ChatMessageCallPermissionButton: React.FC = () => {
  const { formatMessage } = useIntl();
  const theme = useTheme();
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Card className={classes.card}>
      <CardContent className={classes.content}>
        <div className={classes.header}>
          <SvgIcon
            width="34"
            height="34"
            viewBox="0 0 34 34"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="34" height="34" rx="17" fill="none" />
            <path
              d="M17.8264 16.1736V11.2153H19.4792V13.3639L23.6111 9.23193L24.7681 10.3889L20.6361 14.5208H22.7847V16.1736H17.8264ZM23.5698 24.4375C21.8481 24.4375 20.1472 24.0622 18.4668 23.3115C16.7865 22.5609 15.2577 21.4969 13.8804 20.1196C12.5031 18.7423 11.4391 17.2135 10.6885 15.5331C9.93782 13.8528 9.5625 12.1518 9.5625 10.4302C9.5625 10.1823 9.64514 9.97568 9.81042 9.81041C9.97569 9.64513 10.1823 9.56249 10.4302 9.56249H13.7771C13.9699 9.56249 14.1421 9.62791 14.2936 9.75876C14.4451 9.8896 14.5346 10.0445 14.5622 10.2236L15.0993 13.116C15.1269 13.3363 15.12 13.5223 15.0786 13.6738C15.0373 13.8253 14.9616 13.9561 14.8514 14.0663L12.8474 16.091C13.1229 16.6006 13.45 17.093 13.8287 17.5681C14.2075 18.0433 14.6241 18.5013 15.0786 18.942C15.5056 19.369 15.9532 19.7649 16.4215 20.1299C16.8898 20.4949 17.3856 20.8289 17.909 21.1319L19.851 19.1899C19.975 19.066 20.1368 18.973 20.3365 18.911C20.5363 18.849 20.7325 18.8318 20.9253 18.8594L23.7764 19.4378C23.9692 19.4929 24.1276 19.5928 24.2516 19.7374C24.3755 19.882 24.4375 20.0439 24.4375 20.2229V23.5698C24.4375 23.8177 24.3549 24.0243 24.1896 24.1896C24.0243 24.3549 23.8177 24.4375 23.5698 24.4375ZM12.0623 14.5208L13.4259 13.1573L13.0747 11.2153H11.2359C11.3048 11.78 11.4012 12.3378 11.5252 12.8887C11.6491 13.4396 11.8282 13.9837 12.0623 14.5208ZM19.4585 21.917C19.9957 22.1511 20.5431 22.3371 21.101 22.4748C21.6588 22.6125 22.22 22.7021 22.7847 22.7434V20.9253L20.8427 20.5328L19.4585 21.917Z"
              fill={theme.palette.text.primary}
            />
          </SvgIcon>

          <Typography className={classes.title}>
            {formatMessage({
              id: 'CallPermission.chatMessage.CallPermissions.template.title',
            })}
          </Typography>
        </div>

        <Divider className={classes.divider} />

        <Button
          className={classes.button}
          onClick={handleClick}
          endIcon={<ExpandMoreIcon />}
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%', // Needed for flex centering to work
          }}
        >
          {formatMessage({
            id: 'CallPermission.chatMessage.CallPermissions.template.selectLabel',
          })}
        </Button>

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          className={classes.menu}
          getContentAnchorEl={null}
          anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'left',
          }}
          transformOrigin={{
            vertical: 'top',
            horizontal: 'left',
          }}
        >
          <MenuItem className={classes.menuItem}>
            {formatMessage({
              id: 'CallPermission.chatMessage.CallPermissions.template.selectOption.1',
            })}
          </MenuItem>
          <MenuItem className={classes.menuItem}>
            {formatMessage({
              id: 'CallPermission.chatMessage.CallPermissions.template.selectOption.2',
            })}
          </MenuItem>
          <MenuItem className={classes.menuItem}>
            {formatMessage({
              id: 'CallPermission.chatMessage.CallPermissions.template.selectOption.3',
            })}
          </MenuItem>
        </Menu>
      </CardContent>
    </Card>
  );
};

const useStyles = makeStyles(theme => ({
  card: {
    color: theme.palette.text.primary,
    maxWidth: 600,
    marginTop: '8px',
    borderRadius: 16,
    backgroundColor: 'transparent',
    backgroundImage: 'none',
    boxShadow: 'none',
    border: 'none',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '4px',
    padding: '10px 16px',
    backgroundColor: 'rgba(0, 0, 0, 0.04)',
    borderRadius: '4px',
  },
  content: {
    padding: '8px',
    '&.MuiCardContent-root:last-child': {
      paddingBottom: 8,
    },
  },
  phoneIcon: {
    fontSize: 50,
    color: theme.palette.text.primary,
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  divider: {
    marginBottom: '8px',
  },
  button: {
    width: '100%',
    justifyContent: 'space-between',
    textTransform: 'none',
    fontSize: 14,
    fontWeight: 600,
    borderRadius: 8,
    color: '#00C853',
    '&:hover': {
      backgroundColor: 'rgba(0, 200, 83, 0.04)',
    },
  },
  menu: {
    marginTop: '8px',
  },
  menuItem: {
    fontSize: 16,
    padding: '12px 24px',
    minWidth: 250,
  },
}));

export default ChatMessageCallPermissionButton;
